import { Injectable } from '@angular/core';

import { ResetForm } from '@ngxs/form-plugin';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';

import { BlockedSlotService } from '@bayclubs/court-booking-employee-api';
import { BcStateForm, BcStateListContext, Spinner } from '@bayclubs/state';
import { TimeService } from '@bayclubs/ui-core';

import { BlockedSlotDefinitionType } from '@shared-models/shared-models.module';
import { BlockedSlotSaveFormModel } from '../models/blocked-slot-save-form.model';
import { BlockedSlotSaveInputModel } from '../models/blocked-slot-save-input.model';
import { BlockedSlotSaveMapperService } from '../services/blocked-slot-save-mapper.service';
import * as actions from './blocked-slot-save.actions';
import { BLOCKED_SLOT_SAVE_FEATURE_KEY, BlockedSlotSaveStateModel } from './blocked-slot-save.state.model';

const initialState = {
    form: BcStateForm.Empty(),
    spinner: Spinner.Empty(),
    clubCourts: BcStateListContext.Empty(),
    blockedSlotTypes: BcStateListContext.Loaded([
        BlockedSlotDefinitionType.clinic,
        BlockedSlotDefinitionType.league,
        BlockedSlotDefinitionType.lesson,
        BlockedSlotDefinitionType.liveBall,
        BlockedSlotDefinitionType.maintenance,
        BlockedSlotDefinitionType.openPlay,
        BlockedSlotDefinitionType.pickleballEvent,
        BlockedSlotDefinitionType.pickleballTournament,
        BlockedSlotDefinitionType.privateEvent,
        BlockedSlotDefinitionType.tennisTournament,
        BlockedSlotDefinitionType.usta,
        BlockedSlotDefinitionType.other
    ])
} as BlockedSlotSaveStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: BLOCKED_SLOT_SAVE_FEATURE_KEY, defaults: initialState })
export class BlockedSlotSaveState {
    constructor(
        private blockedSlotService: BlockedSlotService,
        private mapperService: BlockedSlotSaveMapperService,
        private timeService: TimeService
    ) { }

    @Action(actions.LoadFormAction)
    loadForm(ctx: StateContext<BlockedSlotSaveStateModel>, action: actions.LoadFormAction) {

        const input = {
            ...action.input,
            date: action.input.date ? action.input.date : this.timeService.toBcDate(this.timeService.nowUtc())
        } as BlockedSlotSaveInputModel;

        ctx.dispatch(
            new ResetForm({ path: 'blockedSlotSave.form', value: BlockedSlotSaveFormModel.init(input) })
        );
    }

    @Action(actions.LoadCourtsAction)
    loadCourts(ctx: StateContext<BlockedSlotSaveStateModel>, action: actions.LoadCourtsAction) {
        const state = ctx.getState();

        const request = {
            clubId: action.clubId ? action.clubId : state.form.model.clubId,
            date: action.date ? action.date.value : state.form.model.date.value
        };

        ctx.patchState({ clubCourts: BcStateListContext.Loading() });
        return this.blockedSlotService.api10ClubIdBlockedslotAddBlockedSlotCourtsGet(request).pipe(
            tap(response => {
                ctx.patchState({ clubCourts: BcStateListContext.Loaded(response.items) });

            }),
            catchError(err => {
                ctx.patchState({ clubCourts: BcStateListContext.Error() });
                throw err;
            })
        );
    }

    @Action(actions.SaveBlockedSlotAction)
    saveBlockedSlot(ctx: StateContext<BlockedSlotSaveStateModel>) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const state = ctx.getState();
        const command = this.mapperService.createSaveCommand(state.form.model);
        return this.blockedSlotService.api10ClubIdBlockedslotPost(command).pipe(
            tap(value => {
                ctx.dispatch(new actions.SaveBlockedSlotSuccessAction());
            }),
            catchError(err => {
                ctx.patchState({ spinner: Spinner.Empty() });
                throw err;
            })
        );
    }

    @Action(actions.SaveBlockedSlotSuccessAction)
    saveBlockedSlotSuccess(ctx: StateContext<BlockedSlotSaveStateModel>) {
        ctx.patchState({ spinner: Spinner.Empty() });
    }

    @Action(actions.ClearSaveFormAction)
    clearSaveForm(ctx: StateContext<BlockedSlotSaveStateModel>) {
        ctx.patchState({
            form: BcStateForm.Empty(),
            clubCourts: BcStateListContext.Empty(),
            blockedSlotTypes: initialState.blockedSlotTypes
        });
    }
}
